import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const HUB_ORIGIN = '50% 50%';
const PILL_OFFSET = { x: 48, y: 28 };

/**
 * @param {HTMLElement} pill
 */
const pillEntranceFrom = (pill) => {
	if (pill.classList.contains('history-values__pill--tr')) {
		return { x: PILL_OFFSET.x, y: -PILL_OFFSET.y };
	}
	if (pill.classList.contains('history-values__pill--br')) {
		return { x: PILL_OFFSET.x, y: PILL_OFFSET.y };
	}
	if (pill.classList.contains('history-values__pill--bl')) {
		return { x: -PILL_OFFSET.x, y: PILL_OFFSET.y };
	}
	return { x: -PILL_OFFSET.x, y: -PILL_OFFSET.y };
};

/**
 * @param {HTMLElement} section
 */
const bindSection = (section) => {
	const visual = section.querySelector('.js-history-values-visual');
	if (!visual) {
		return null;
	}

	const orbitFills = visual.querySelectorAll('.history-values__orbit-fill');
	const orbits = visual.querySelectorAll('.history-values__orbit');
	const hubGlow = visual.querySelector('.history-values__hub-glow');
	const hub = visual.querySelector('.history-values__hub');
	const hubLogo = visual.querySelector('.history-values__hub-logo');
	const pills = visual.querySelectorAll('.history-values__pill');
	const introBits = section.querySelectorAll(
		'.history-values__intro .text-eyebrow, .history-values__intro .text-h2, .history-values__milestone, .history-values__cta',
	);

	const orbitTargets = [...orbitFills, ...orbits];

	gsap.set(orbitTargets, { transformOrigin: HUB_ORIGIN, scale: 0.72, autoAlpha: 0 });
	gsap.set(hubGlow, { transformOrigin: '50% 50%', scale: 0.4, autoAlpha: 0 });
	// Hub lives inside inner orbit — avoid double scale (orbit tween scales hub with ring)
	gsap.set(hub, { autoAlpha: 0 });
	if (hubLogo) {
		gsap.set(hubLogo, { y: 10, autoAlpha: 0 });
	}
	gsap.set(pills, { autoAlpha: 0 });
	pills.forEach((pill) => {
		const from = pillEntranceFrom(pill);
		gsap.set(pill, { x: from.x, y: from.y, scale: 0.92 });
	});
	if (introBits.length) {
		gsap.set(introBits, { y: 22, autoAlpha: 0 });
	}

	let idleTimeline = null;

	const entrance = gsap.timeline({
		scrollTrigger: {
			trigger: section,
			start: 'top 78%',
			once: true,
		},
		defaults: { ease: 'power3.out' },
		onComplete: () => {
			idleTimeline = gsap.timeline({ repeat: -1, yoyo: true, defaults: { ease: 'sine.inOut' } });
			if (hubGlow) {
				idleTimeline.to(hubGlow, { scale: 1.06, opacity: 0.92, duration: 2.8 }, 0);
			}
			idleTimeline.to(
				visual.querySelector('.history-values__orbit--outer'),
				{ opacity: 0.62, duration: 3.4 },
				0,
			);
			idleTimeline.to(
				visual.querySelector('.history-values__orbit--inner'),
				{ opacity: 0.72, duration: 3.4 },
				0,
			);
		},
	});

	if (introBits.length) {
		entrance.to(introBits, { y: 0, autoAlpha: 1, duration: 0.7, stagger: 0.09 }, 0);
	}

	entrance
		.to(hubGlow, { scale: 1, autoAlpha: 1, duration: 0.85, ease: 'power2.out' }, 0.12)
		.to(
			orbitFills,
			{ scale: 1, autoAlpha: 1, duration: 1, stagger: 0.1, ease: 'power2.out' },
			0.2,
		)
		.to(
			visual.querySelector('.history-values__orbit--outer'),
			{ scale: 1, autoAlpha: 1, duration: 0.95, ease: 'back.out(1.35)' },
			0.28,
		)
		.to(
			visual.querySelector('.history-values__orbit--inner'),
			{ scale: 1, autoAlpha: 1, duration: 0.9, ease: 'back.out(1.5)' },
			0.42,
		)
		.to(hub, { autoAlpha: 1, duration: 0.55, ease: 'power2.out' }, 0.52);

	if (hubLogo) {
		entrance.to(hubLogo, { y: 0, autoAlpha: 1, duration: 0.55 }, 0.68);
	}

	entrance.to(
		pills,
		{
			x: 0,
			y: 0,
			scale: 1,
			autoAlpha: 1,
			duration: 0.65,
			stagger: 0.1,
			ease: 'back.out(1.35)',
		},
		0.48,
	);

	return () => {
		entrance.scrollTrigger?.kill();
		idleTimeline?.kill();
		gsap.killTweensOf([...orbitTargets, hubGlow, hub, hubLogo, ...pills, ...introBits]);
		gsap.set(
			[...orbitTargets, hubGlow, hub, hubLogo, ...pills, ...introBits].filter(Boolean),
			{ clearProps: 'all' },
		);
	};
};

/**
 * @param {HTMLElement} section
 */
const bindSectionReduced = (section) => {
	const visual = section.querySelector('.js-history-values-visual');
	const introBits = section.querySelectorAll(
		'.history-values__intro .text-eyebrow, .history-values__intro .text-h2, .history-values__milestone, .history-values__cta',
	);
	if (!visual) {
		return null;
	}

	const targets = [visual, ...introBits];
	gsap.set(targets, { autoAlpha: 0 });

	const tween = gsap.to(targets, {
		autoAlpha: 1,
		duration: 0.55,
		stagger: 0.06,
		ease: 'power2.out',
		scrollTrigger: {
			trigger: section,
			start: 'top 85%',
			once: true,
		},
	});

	return () => {
		tween.scrollTrigger?.kill();
		gsap.killTweensOf(targets);
		gsap.set(targets, { clearProps: 'all' });
	};
};

export const initHistoryValuesHubAnimation = () => {
	const sections = document.querySelectorAll('.js-history-values');
	if (!sections.length) {
		return;
	}

	const cleanups = [];

	gsap.matchMedia().add('(prefers-reduced-motion: no-preference) and (min-width: 640px)', () => {
		sections.forEach((section) => {
			const cleanup = bindSection(section);
			if (cleanup) {
				cleanups.push(cleanup);
			}
		});

		return () => cleanups.forEach((fn) => fn());
	});

	gsap.matchMedia().add('(prefers-reduced-motion: no-preference) and (max-width: 639px)', () => {
		sections.forEach((section) => {
			const visual = section.querySelector('.js-history-values-visual');
			if (visual) {
				gsap.set(visual.querySelectorAll('.history-values__pill, .history-values__hub, .history-values__hub-logo'), {
					clearProps: 'transform,x,y,scale',
				});
			}
			const cleanup = bindSectionReduced(section);
			if (cleanup) {
				cleanups.push(cleanup);
			}
		});

		return () => cleanups.forEach((fn) => fn());
	});
};
